import { getFileSize } from 'app/utils/attachments';

const MEGABYTE = 1024 * 1024; // 1MB
const FIVE_MB = 5 * MEGABYTE; // 5MB
const SIXTEEN_MB = 16 * MEGABYTE; // 16MB

/**
 * WhatsApp file type configurations with size limits per Twilio specifications.
 * https://www.twilio.com/docs/whatsapp/guidance-whatsapp-media-messages
 */
const WHATSAPP_FILE_TYPE_CONFIG: Record<string, number> = {
  // Image files (5MB limit)
  jpg: FIVE_MB,
  jpeg: FIVE_MB,
  png: FIVE_MB,
  // Document files (16MB limit)
  pdf: SIXTEEN_MB,
  doc: SIXTEEN_MB,
  docx: SIXTEEN_MB,
  pptx: SIXTEEN_MB,
  xlsx: SIXTEEN_MB,
  // Audio files (16MB limit)
  mp3: SIXTEEN_MB,
  amr: SIXTEEN_MB,
  aac: SIXTEEN_MB,
  // Video files (16MB limit)
  mp4: SIXTEEN_MB,
  // Contact files (16MB limit)
  vcf: SIXTEEN_MB,
};

const supportedTypes = (): string =>
  Object.keys(WHATSAPP_FILE_TYPE_CONFIG)
    .map((type) => `.${type}`)
    .join(', ');

type ValidateWhatsAppAttachmentOptions = {
  file: File;
};

/**
 * Validates a single file against the WhatsApp type and size constraints.
 * Returns null if valid, or the reason it was turned away, worded for the
 * agent.
 *
 * How many attachments a message may carry is not checked here: that is a
 * property of the whole selection, and lives in `validateAttachmentBatch`.
 */
export const validateWhatsAppAttachment = ({
  file,
}: ValidateWhatsAppAttachmentOptions): string | null => {
  // Check file type
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  // `hasOwn`, so that a file named `report.constructor` cannot pick up an
  // inherited member and sail past both this check and the size check below.
  const maxSizeForType =
    fileExtension && Object.hasOwn(WHATSAPP_FILE_TYPE_CONFIG, fileExtension)
      ? WHATSAPP_FILE_TYPE_CONFIG[fileExtension]
      : undefined;

  if (!fileExtension || !maxSizeForType) {
    // A name with no dot leaves `split` returning the name itself, so an
    // extension that matches nothing is not proof that the agent picked one.
    const hasExtension = file.name.includes('.') && !!fileExtension;

    return hasExtension
      ? `.${fileExtension} files can't be sent on WhatsApp. Supported types: ${supportedTypes()}`
      : `Files sent on WhatsApp must have a recognisable type. Supported types: ${supportedTypes()}`;
  }

  // Check file size against the limit for this file type
  if (file.size > maxSizeForType) {
    return `A .${fileExtension} sent on WhatsApp must be under ${getFileSize(maxSizeForType)} — this file is ${getFileSize(file.size)}.`;
  }

  return null;
};
